import { reactive, ref } from 'vue';

const isLoading = ref(false);
const state = reactive({
  isOpen: false,
  title: '',
  message: '',
  confirmText: 'Yes',
  cancelText: 'No',
  confirmVariant: 'critical',
  onConfirm: () => {},
  onCancel: () => {},
});

export function useDialogConfirmation() {
  function reset() {
    Object.assign(state, {
      isOpen: false,
      title: '',
      message: '',
      confirmText: 'Yes',
      cancelText: 'No',
      confirmVariant: 'critical',
      onConfirm: () => {},
      onCancel: () => {},
    });
  }

  function close() {
    state.isOpen = false;

    setTimeout(() => {
      reset();
    }, 300);
  }

  async function handleOnConfirm() {
    if (state.onConfirm) {
      const result = state.onConfirm();

      if (result instanceof Promise) {
        isLoading.value = true;
        await result;
        isLoading.value = false;
      }
    }

    close();
  }

  function handleOnCancel() {
    if (state.onCancel) {
      state.onCancel();
    }

    close();
  }

  return {
    state,
    isLoading,
    reset,
    close,
    handleOnConfirm,
    handleOnCancel,
  };
}

/**
 * @typedef {object} DialogConfirmationOptions
 * @property {string} title - The title of the dialog.
 * @property {string} message - The message to display in the dialog.
 * @property {Function} onConfirm - The function to call when the confirm button is clicked.
 * @property {Function} [onCancel] - The function to call when the cancel button is clicked or the dialog is closed.
 */

/**
 * Sets the properties for the dialog confirmation.
 * @param {DialogConfirmationOptions} options - The options for the dialog.
 */
export function setDialogConfirmation(options) {
  const { title, message, onConfirm, onCancel } = options;

  state.title = title;
  state.message = message;
  state.onConfirm = onConfirm;
  state.onCancel = onCancel;

  if (options.confirmText) {
    state.confirmText = options.confirmText;
  }

  if (options.cancelText) {
    state.cancelText = options.cancelText;
  }

  if (options.confirmVariant) {
    state.confirmVariant = options.confirmVariant;
  }

  state.isOpen = true;
}
